import React from "react";

import type { EventPropGetter } from ".";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { isAfter } from "date-fns";

import { CalendarFilterContext, type Event } from "~/calendar/CalendarPage";
import EventContextMenu from "~/calendar/EventContextMenu";
import { hexToRGB } from "~/lib/utils";
import { timeBlockTypeIcons } from "~/nav/panels/Meetings/utils";
import EventIcons, { getEventIcons } from "~/ui/calendar/EventIcons";

import { DnDContext } from "./addons/dragAndDrop/DnDContext";
import { isLightColor } from "./utils/colors";

import styles from "./DayCellEvent.module.css";

export interface DayCellEventProps {
	event: Event & { __isPreview?: boolean }; // injected in dnd WeekWrapper
	style: {
		top: number;
		height: number;
		width: number;
		xOffset: number;
	};
	selected: boolean;
	continuesPrior: boolean;
	continuesAfter: boolean;
	getters: { eventProp: EventPropGetter<Event> };
	onSelect: (event: Event, e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onDoubleClick: (event: Event, e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	isBackgroundEvent: boolean;
	slotStart: Date;
	slotEnd: Date;
	isAllDay: boolean;
}

export default function DayCellEvent({
	style,
	event,
	selected,
	isAllDay,
	onSelect,
	onDoubleClick,
	continuesPrior,
	continuesAfter,
	getters,
}: DayCellEventProps) {
	const { draggable } = React.useContext(DnDContext);
	const { showTileIcons } = React.useContext(CalendarFilterContext);

	const showAsAllDay = isAllDay || event.isAllDay;

	const userProps = getters.eventProp(event, event.start, event.end, selected);

	const isPastEvent = React.useMemo(() => isAfter(new Date(), event.end), [event.end]);

	const { calendarColor, fellowColor, providerEventColor } = event;

	const { colorStyles, requiresDarkText }: { colorStyles: React.CSSProperties; requiresDarkText: boolean } =
		React.useMemo(() => {
			const colorStyles: React.CSSProperties = {};
			let requiresDarkText = false;

			if (calendarColor) colorStyles["--calendarColor"] = calendarColor;

			const setToCalendarColor = () => {
				if (!calendarColor) return; // Fallback to css defaults

				requiresDarkText = isLightColor(calendarColor);
				colorStyles["--tileColor-rgb"] = hexToRGB(calendarColor);
				colorStyles["--tileColor"] = calendarColor;
			};

			const setToProviderColor = () => {
				if (!providerEventColor) return setToCalendarColor();

				requiresDarkText = isLightColor(providerEventColor);
				colorStyles["--tileColor-rgb"] = hexToRGB(providerEventColor);
				colorStyles["--tileColor"] = providerEventColor;
			};

			const setToFellowColor = () => {
				requiresDarkText = false;
				colorStyles["--tileColor-rgb"] = `var(--event-${fellowColor.toLowerCase()}-rgb)`;
				colorStyles["--tileColor"] = `var(--event-${fellowColor.toLowerCase()})`;
			};

			switch (fellowColor) {
				case "CALENDAR": {
					setToCalendarColor();
					break;
				}
				case "PROVIDER": {
					setToProviderColor();
					break;
				}
				default: {
					setToFellowColor();
					break;
				}
			}

			return {
				colorStyles,
				requiresDarkText,
			};
		}, [calendarColor, fellowColor, providerEventColor]);

	const { draggableCondition, resizableCondition, selectableCondition } = draggable;

	const isDraggable = React.useMemo(() => draggableCondition(event), [event, draggableCondition]);
	const isResizable = React.useMemo(() => resizableCondition(event), [event, resizableCondition]);

	// TODO: The `selected` prop can still make this event selected
	const isSelectable = React.useMemo(() => selectableCondition?.(event) ?? true, [event, selectableCondition]);

	const handleStartDragging = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (e.button !== 0 || !isDraggable) return;
			draggable.onBeginAction(event, "move");
		},
		[draggable, event, isDraggable],
	);

	const handleStartResizing = React.useCallback(
		(direction: "left" | "right") => (e: React.MouseEvent<HTMLSpanElement>) => {
			if (e.button !== 0 || !isResizable) return;

			switch (direction) {
				case "left": {
					return draggable.onBeginAction(event, "resize", "LEFT");
				}
				case "right": {
					return draggable.onBeginAction(event, "resize", "RIGHT");
				}
			}
		},
		[draggable, event, isResizable],
	);

	const isNeedsAction = ["needsAction", "none", "notResponded"].includes(event.userResponseStatus || "");
	const isDifferentColors = colorStyles["--tileColor"] !== colorStyles["--calendarColor"];
	const isTimeBlock = event.timeBlockType === "focusTime" || event.timeBlockType === "outOfOffice";
	const eventIcons = getEventIcons(event, showTileIcons);

	return (
		<EventContextMenu disabled={event.blockType !== "real" || !isSelectable} event={event}>
			<div
				tabIndex={0}
				style={{ ...userProps.style, ...colorStyles, ...style }}
				onClick={e => onSelect(event, e)}
				onDoubleClick={e => onDoubleClick(event, e)}
				data-event-type={event.blockType}
				className={cx(styles.allDayEvent, "fbc-event", styles[event.userResponseStatus ?? ""], {
					[styles.notSelectable]: !isSelectable,
					[styles.selected]: selected,
					[styles.allDay]: showAsAllDay,
					[styles.continuesBefore]: continuesPrior,
					[styles.continuesAfter]: continuesAfter,
					[styles.pastEvent]: isPastEvent,
					[styles.filteredOut]: event.filteredOut,
					[styles.cancelled]: event.status === "cancelled",
					[styles.isParticipant]: event.blockType === "participant",
					[styles.imaginaryEvent]: event.blockType === "imaginary",
					[styles.isLightColor]: requiresDarkText,
					[styles.withBar]: isNeedsAction && isDifferentColors,
					"fbc-addons-dnd-dragged-event":
						draggable.dragAndDropAction?.interacting && draggable.dragAndDropAction.event === event,
					// eslint-disable-next-line no-underscore-dangle
					"fbc-addons-dnd-drag-preview": event.__isPreview,
				})}
			>
				{!continuesPrior && <span className={styles.resizeGrip} onMouseDown={handleStartResizing("left")} />}
				<div className={styles.eventContent} onMouseDown={handleStartDragging}>
					<div title={event.title}>
						<div className={styles.calendarBar} />
						<div className={styles.eventTitleContainer}>
							{isTimeBlock && <FontAwesomeIcon fixedWidth icon={timeBlockTypeIcons[event.timeBlockType]} />}
							<div className={styles.eventTitle}>{event.title}</div>
						</div>
					</div>
					<EventIcons icons={eventIcons} />
				</div>
				{!continuesAfter && <span className={styles.resizeGrip} onMouseDown={handleStartResizing("right")} />}
			</div>
		</EventContextMenu>
	);
}
